import { useEffect, useMemo, useRef, useState } from "react";
import { AppStateContextType, AppStateProviderProps } from "./app-state-types";
import { AppState } from "@/types/app-state/app-state-types";
import AppStateContext from "./context";
import { getAppMonitorStatus } from "@/services/app-monitor-status/app-monitor-status";
import { useReactiveState } from "../buildDinamycState";

const HEALTH_CHECK_INTERVAL = import.meta.env.VITE_HEALTH_CHECK_INTERVAL ?? 10000;


const AppStateProvider: React.FC<AppStateProviderProps> = ({ children, defaultValues }) => {
    const [appState, setAppState] = useState<AppState>({
        isHealthy: true,
        message: '',
        code: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const checkApplicationHealth = async () => {
        try {
            setLoading(true);
            const domain = window.location.hostname;
            const data = await getAppMonitorStatus({ 'app_id': 1, 'website': domain });
            
            setAppState({
                isHealthy: data.is_healthy,
                message: data.message,
                code: data.code
            });
            setError(null);
        } catch (error) {
            console.error("Error checking application health", error);
            setError('Failed to check application health');
            setAppState(prev => ({
                ...prev,
                isHealthy: false,
                message: 'Error de conexión con el servidor',
                code: 500
            }));
        } finally {
            setLoading(false);
        }
    };

    const firstRender = useRef(true);

    useEffect(() => {
        if (!firstRender.current) {
            return;
        }

        if (Object.keys(defaultValues?.appState ?? {}).length > 0) {
            return;
        }

        firstRender.current = false;

        checkApplicationHealth();

        const interval = setInterval(checkApplicationHealth, HEALTH_CHECK_INTERVAL);

        
        return () => clearInterval(interval);
    }, [defaultValues]);

    const value = useMemo(
        () => ({ appState, setAppState, loading, error }),
        [appState, loading, error]
    );

    const defaultValuesReactive = useReactiveState(defaultValues ?? {}) as AppStateContextType;

    return (
        <AppStateContext.Provider value={Object.keys(defaultValuesReactive)?.length > 0 ? defaultValuesReactive : value}>
        {children}
        </AppStateContext.Provider>
    );
};

export default AppStateProvider;